import React, { useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { NormalInput } from '../../../shared/component/headless/input/InputBody';
import HeadlessTextArea from '../../../shared/component/headless/textarea/HeadlessTextArea';
import { NormalTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import { AlertPosition, AlertType, TextAlignment } from '../../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH } from '../../../shared/constants/limits';
import { sanitizeString } from '../../../shared/util/sanitize';
import { useAdminMember } from '../../share/hook/useAdminMember';
import { UniversalCheckbox } from '../../../shared/component/UniversalCheckbox';

const MARKDOWN_TEMPLATES = [
  { label: 'Username', value: '{{username}}' },
  { label: 'Bold', value: '**bold text**' },
  { label: 'Link', value: '[link text](https://example.com)' },
  { label: 'Heading', value: '## Heading' },
  { label: 'List', value: '- List item' },
  { label: 'Quote', value: '> Quote' },
];

const AdminMemberEmailViewer: React.FC = () => {
  const {
    activeMember,
    selectedEmailMembers,
    sendMemberEmail,
    closeEmailViewer,
  } = useAdminMember();
  const { enqueueAlert, setTextLengthExceededAlert } = useStandardAlertStore();
  const [title, setTitle] = useState('[Architrave] ');
  const [body, setBody] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [saveEmail, setSaveEmail] = useState(false);

  const targets = selectedEmailMembers.length > 0
    ? selectedEmailMembers
    : activeMember
      ? [activeMember]
      : [];

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH) {
      setTextLengthExceededAlert(ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH);
      return;
    }
    setBody(e.target.value);
  };

  const handleAppendMarkdownTemplate = (template: string) => {
    const separator = body.length === 0 ? '' : body.endsWith('\n') ? '\n' : '\n\n';
    const nextBody = `${body}${separator}${template}`;

    if (nextBody.length > ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH) {
      setTextLengthExceededAlert(ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH);
      return;
    }

    setBody(nextBody);
  };

  const handleSubmit = async () => {
    const sanitizedTitle = sanitizeString(title);
    const trimmedBody = body.trim();

    if (!sanitizedTitle || !trimmedBody || (!sendToAll && targets.length === 0)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    const isSent = await sendMemberEmail({
      targetIdList: sendToAll ? [] : targets.map((member) => member.memberId),
      sendToAll,
      saveEmail,
      title: sanitizedTitle,
      body: trimmedBody,
    });

    if (isSent) {
      closeEmailViewer();
    }
  };

  const targetTitle = sendToAll
    ? 'Email to Everyone'
    : targets.length === 1
      ? `Email to ${targets[0].username}`
      : `Email to ${targets.length} members`;
  const targetMeta = sendToAll
    ? 'All members'
    : targets.map((member) => member.username).join(', ');

  return (
    <EmailViewerComp>
      <Header>
        <Title onClick={() => setSendToAll((prev) => !prev)}>
          {targetTitle}         
        </Title>
        <Meta>{targetMeta || 'Select members from the list'}</Meta>
      </Header>
      <HeadlessInput
        type="text"
        value={title}
        handleChange={(e) => setTitle(e.target.value)}
        placeholder="Subject"
        StyledInput={NormalInput}
      />

      <MarkdownContainer>
        {MARKDOWN_TEMPLATES.map((template) => (
          <HeadlessBtn
            key={template.label}
            value={template.label}
            handleClick={() => handleAppendMarkdownTemplate(template.value)}
            StyledBtn={MarkdownTemplateBtn}
          />
        ))}
      </MarkdownContainer>
      <DescriptionWrapper>
        <LimitDiv>
          {body.length} / {ADMIN_MEMBER_EMAIL_BODY_MAX_LENGTH}
        </LimitDiv>
        <HeadlessTextArea
          alignment={TextAlignment.LEFT}
          content={body}
          placeholder="Markdown body"
          handleChange={handleBodyChange}
          StyledTextArea={NormalTextArea}
          $minRows={12}
          $maxRows={18}
        />
      </DescriptionWrapper>
      <CheckboxComp>
        <UniversalCheckbox
          checked={saveEmail}
          onChange={(e) => setSaveEmail(e.target.checked)}
        />
        Save email
      </CheckboxComp>
      <BtnContainer>
        <HeadlessBtn
          value="Send"
          handleClick={handleSubmit}
          StyledBtn={BlackFitBtn}
        />
        <HeadlessBtn
          value="Cancel"
          handleClick={closeEmailViewer}
          StyledBtn={PaperBlurFitBtn}
        />
      </BtnContainer>
    </EmailViewerComp>
  );
};

const EmailViewerComp = styled.section`
  position: relative;
  width: 29vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  padding-right: 2px;
  padding-bottom: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
`;

const Title = styled.h2`
  width: 100%;
  min-height: 28px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
  cursor: pointer;
`;

const Meta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const DescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
`;

const MarkdownContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: -8px;
`;

const MarkdownTemplateBtn = styled(PaperBlurFitBtn)`
  padding: 6px 10px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: -8px;
  margin-bottom: -8px;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const LimitDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  z-index: 2;
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 1vh;
  margin-top: 2.5vh;
`;

export default AdminMemberEmailViewer;
